<template>
  <div>
    <b-card no-body>
      <!-- Head -->
      <table-header :actions="['buttonAdd', 'buttonMore']">
        <template #buttonAdd>
          <b-button
            v-if="canAccess('manage', 'Banner')"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :to="{ name: 'banners-create'}"
            class="mr-1"
          >
            + Buat Banner
          </b-button>
        </template>
        <template #buttonMore>
          <table-action
            :actions="tableHeaderAction"
            :data="{}"
          />
        </template>
      </table-header>
      <!-- Alert -->
      <div
        v-if="fixedSearchKeyword"
        class="mx-2"
      >
        <b-alert
          variant="primary"
          show
        >
          Menampilkan hasil untuk "{{ fixedSearchKeyword }}"
        </b-alert>
      </div>
      <!-- Body -->
      <b-skeleton-table
        v-if="isPending"
        :rows="5"
        :columns="4"
      />
      <b-table
        v-else
        ref="refUserListTable"
        :items="bannerList.data"
        responsive
        :fields="tableFields"
        primary-key="id"
        show-empty
        empty-text="Tidak ada data yang ditemukan"
        class="position-relative mh-160"
      >
        <!-- Column: Image -->
        <template #cell(image)="data">
          <b-media
            vertical-align="center"
          >
            <template #aside>
              <b-img
                rounded
                width="50"
                :src="linkStaging(data.item.image, 50, 50)"
                :alt="data.item.name"
                @error="(event) => {
                  event.target.src = require('@/assets/images/icons/broken-image.svg')
                }"
              />
            </template>
          </b-media>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          {{ data.item.name }}
        </template>

        <!-- Column: Link -->
        <template #cell(link)="data">
          <a
            v-if="data.item.link"
            v-b-tooltip.hover.top="'Buka di halaman baru'"
            target="_blank"
            :href="`${data.item.link}`"
          >{{ data.item.link }}</a>
          <span v-else>-</span>
        </template>

        <!-- Column: Type -->
        <template #cell(type)="data">
          {{ data.item.type }}
        </template>

        <!-- Column: Active -->
        <template #cell(active)="data">
          <b-form-checkbox
            v-if="canAccess('manage', 'Banner')"
            v-model="data.item.active"
            :checked="true"
            class="mt-0"
            name="active"
            switch
            inline
            @change="activated(data.item, $event)"
          />

          <div v-else>
            <b-badge
              v-if="data.item.active"
              variant="light-primary"
              class="mb-1 mr-1"
            >
              Aktif
            </b-badge>
          </div>
        </template>

        <!-- Column: Actions -->
        <template
          v-if="canAccess('manage', 'Banner')"
          #cell(actions)="data"
        >
          <div class="text-nowrap">
            <table-action
              :actions="tableAction"
              :data="data"
            />
          </div>
        </template>

      </b-table>
      <!-- Footer -->
      <div class="mx-2 mb-2">
        <pagination
          :data="bannerList.meta"
          :paylod="{ keyword: fixedSearchKeyword || '' }"
          action="banner/getBannerList"
        />
      </div>
    </b-card>

    <b-modal
      v-if="canAccess('manage', 'Banner')"
      id="modal-delete"
      title="Apakah anda yakin"
      @hidden="deletedData = ''"
    >
      Ingin Menghapus Banner ini?
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="isDeleting"
          @click="cancel()"
        >
          Batalkan
        </b-button>
        <b-button
          variant="danger"
          :disabled="isDeleting"
          @click.prevent="handleDelete"
        >
          <b-spinner
            v-if="isDeleting"
            small
            label="Spinning"
          />
          {{ isDeleting ? 'Menghapus' : 'Ya, Hapus' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import {
  BSkeletonTable,
  BAlert,
  BCard,
  BButton,
  BTable,
  BImg,
  BMedia,
  VBTooltip,
  BFormCheckbox,
  BBadge,
} from 'bootstrap-vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'
import TableAction from '@/layouts/components/custom/TableAction.vue'

export default {
  components: {
    BAlert,
    BSkeletonTable,
    BCard,
    BButton,
    BTable,
    BImg,
    BMedia,
    BFormCheckbox,
    BBadge,
    TableHeader,
    TableAction,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tableFields: [
        { key: 'image', label: 'Image' },
        { key: 'name', label: 'Nama' },
        { key: 'link', label: 'Url' },
        { key: 'type', label: 'Tipe' },
        { key: 'active', label: 'Aktif' },
        { key: 'actions', label: '' },
      ],
      searchKeyword: '',
      fixedSearchKeyword: '',
      isPending: false,
      isDeleting: false,
      deletedData: {},
    }
  },
  computed: {
    ...mapState('banner', ['bannerList']),
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: this.canAccess('manage', 'Banner'),
          to: 'banners-edit|id:id',
          action: '',
          icon: 'EditIcon',
          title: 'Edit',
        },
        {
          access: null,
          to: null,
          action: data => { this.deleteConfirmation(data.id) },
          icon: 'TrashIcon',
          title: 'Hapus',
        },
      ]
    },
    tableHeaderAction() {
      return [
        {
          access: this.canAccess('manage', 'Banner'),
          to: 'banners-sort',
          action: '',
          icon: 'MenuIcon',
          title: 'Urutan',
        },
      ]
    },
  },
  async created() {
    this.isPending = true
    await this.getBannerList(this.query)
    this.isPending = false
  },
  methods: {
    ...mapActions('banner', ['getBannerList', 'updateActivated', 'deleteBanner']),
    showDetailModal(item) {
      this.userDetail = item
      this.$bvModal.show('modal-user-detail')
    },
    activated(value, $event) {
      this.updateActivated({
        bannerId: value.id, data: value, value: $event,
      })
    },
    deleteConfirmation(value) {
      this.$bvModal.show('modal-delete')
      this.deletedData = value
    },
    async handleDelete() {
      this.isDeleting = true
      await this.deleteBanner({ bannerId: this.deletedData })
      this.isDeleting = false
      this.$bvModal.hide('modal-delete')
    },
  },
}
</script>
